import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { CheckCircle2 } from 'lucide-react';
import { images } from '../utils/images';

const NDISProviderSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section 
      ref={sectionRef}
      className={`py-20 bg-white transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <div className="container mx-auto px-4">
        {/* What Is an NDIS Support Provider */}
        <div className="max-w-4xl mx-auto mb-20">
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-center"
            style={{ color: '#587740' }}
          >
            What Is an NDIS Support Provider?
          </h2>
          
          {/* Featured consulting image */}
          <div className="rounded-2xl overflow-hidden shadow-xl mb-8">
            <img 
              src={images.supportCoordinator2}
              alt="Life Care and Wellness NDIS support provider"
              className="w-full h-[300px] md:h-[400px] object-cover"
            />
          </div>
          
          <p 
            className="text-lg leading-relaxed text-center mb-8"
            style={{ color: '#5e5e5e' }}
          >
            Navigating the National Disability Insurance Scheme (NDIS) can feel overwhelming without 
            the right guidance. An NDIS Service Provider is an organisation or individual approved to 
            deliver funded services to participants under the NDIS.
          </p>
          <p 
            className="text-lg leading-relaxed text-center"
            style={{ color: '#5e5e5e' }}
          >
            These providers ensure that your goals, abilities, and choices are always at the forefront 
            of your support plan.
          </p>
        </div>

        {/* Three Column Section */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-20">
          {/* Role of Provider */}
          <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-8">
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
                style={{ backgroundColor: '#AABC5A' }}
              >
                <CheckCircle2 className="w-6 h-6 text-white" />
              </div>
              <h3 
                className="text-xl font-bold mb-4"
                style={{ color: '#587740' }}
              >
                Role of an NDIS Provider
              </h3>
              <p 
                className="text-base leading-relaxed"
                style={{ color: '#5e5e5e' }}
              >
                Your NDIS provider plays a central role in your overall journey. From helping you with 
                personal care to supporting you in building life skills or accessing your community, a 
                quality provider ensures your needs are met with empathy and expertise.
              </p>
            </CardContent>
          </Card>

          {/* Choosing the Right Provider */}
          <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-8">
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
                style={{ backgroundColor: '#7A993B' }}
              >
                <CheckCircle2 className="w-6 h-6 text-white" />
              </div>
              <h3 
                className="text-xl font-bold mb-4"
                style={{ color: '#587740' }}
              >
                Choosing the Right Provider
              </h3>
              <p 
                className="text-base leading-relaxed"
                style={{ color: '#5e5e5e' }}
              >
                The right NDIS provider makes all the difference. Look for a team that listens, communicates 
                clearly, and genuinely cares about your progress. At Life Care and Wellness, we build real 
                relationships with the people we support — because you deserve more than just a service.
              </p>
            </CardContent>
          </Card>

          {/* Our Services */}
          <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-8">
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
                style={{ backgroundColor: '#6c935e' }}
              >
                <CheckCircle2 className="w-6 h-6 text-white" />
              </div>
              <h3 
                className="text-xl font-bold mb-4"
                style={{ color: '#587740' }}
              >
                Our Support Services
              </h3>
              <p 
                className="text-base leading-relaxed"
                style={{ color: '#5e5e5e' }}
              >
                We proudly serve individuals across Logan and the greater Brisbane region with 
                comprehensive support options designed to enhance independence and participation. Our 
                approach is flexible and responsive.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Core Support & Daily Living */}
        <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Core Support Services */}
          <div>
            <h3 
              className="text-2xl md:text-3xl font-bold mb-6"
              style={{ color: '#587740' }}
            >
              Core Support Services We Offer
            </h3>
            <div className="space-y-4 mb-6">
              {[
                'Personal care and grooming',
                'Meal preparation and nutrition support',
                'Assistance with mobility',
                'Community access and transport'
              ].map((item, idx) => (
                <div key={idx} className="flex items-start">
                  <CheckCircle2 
                    className="w-5 h-5 mt-1 mr-3 flex-shrink-0" 
                    style={{ color: '#81b956' }}
                  />
                  <p className="text-base" style={{ color: '#5e5e5e' }}>{item}</p>
                </div>
              ))}
            </div>
            <p className="text-base leading-relaxed" style={{ color: '#5e5e5e' }}>
              As a trusted NDIS Provider in Logan and Brisbane, our services are tailored to meet your 
              daily needs while promoting dignity and choice.
            </p>
          </div>

          {/* Daily Living Support */}
          <div>
            <h3 
              className="text-2xl md:text-3xl font-bold mb-6"
              style={{ color: '#587740' }}
            >
              Daily Living and In-Home Support
            </h3>
            <p className="text-base leading-relaxed mb-6" style={{ color: '#5e5e5e' }}>
              Maintaining comfort and safety at home is essential. Our team provides in-home services 
              that help participants live independently, with confidence and dignity.
            </p>
            <div className="space-y-4">
              {[
                'General household cleaning',
                'Grocery shopping and errands',
                'Gardening and minor home maintenance',
                'Personal care and hygiene assistance'
              ].map((item, idx) => (
                <div key={idx} className="flex items-start">
                  <CheckCircle2 
                    className="w-5 h-5 mt-1 mr-3 flex-shrink-0" 
                    style={{ color: '#81b956' }}
                  />
                  <p className="text-base" style={{ color: '#5e5e5e' }}>{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NDISProviderSection;
